import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';

// api's
import { getUsersByRole } from '../../api/userApi';
import { getGrades } from '../../../api/gradeApi';

const StudentDetails = ({ user, setUser }) => {
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gradeOptions, setGradeOptions] = useState([]);
  

  useEffect(() => {
    const fetchParents = async () => {
      setLoading(true);
      try {
        // Parent role has ID 6 based on the role seeder
        const parentRoleId = 6;
        const parentsData = await getUsersByRole(parentRoleId);
        
        // Format parents for dropdown
        const formattedParents = parentsData.map(parent => ({
          label: `${parent.name} (${parent.email})`,
          value: parent.id
        }));
        
        setParents([
          { label: 'Select Parent', value: null },
          ...formattedParents
        ]);
      } catch (error) {
        console.error('Error fetching parents:', error);
        // Set empty options if fetch fails
        setParents([{ label: 'No parents available', value: null }]);
      } finally {
        setLoading(false);
      }
    };

    fetchParents();
  }, []);

  useEffect(() => {
    const fetchGrades = async () => {
      try {
        const data = await getGrades();
        const opts = (Array.isArray(data) ? data : data?.data || [])
          .filter(Boolean)
          .map(g => ({ label: g.name || g.code || `Grade ${g.id}` , value: g.id, name: g.name }));
        setGradeOptions([{ label: 'Select Grade/Class', value: null }, ...opts]);

        // If editing or user already has grade by name but missing gradeId, sync it
        if ((user?.gradeId == null) && user?.grade) {
          const match = opts.find(o => (o.name || o.label) === user.grade);
          if (match) {
            setUser(prev => ({ ...prev, gradeId: match.value }));
          }
        }
      } catch (err) {
        console.error('Error fetching grades:', err);
        setGradeOptions([{ label: 'No grades available', value: null }]);
      }
    };
    fetchGrades();
  }, []);

  useEffect(() => {
    if ((user?.gradeId == null) && user?.grade && Array.isArray(gradeOptions) && gradeOptions.length > 0) {
      const actualOptions = gradeOptions.filter(o => o.value !== null);
      const match = actualOptions.find(o => (o.name || o.label) === user.grade);
      if (match) {
        setUser(prev => ({ ...prev, gradeId: match.value }));
      }
    }
  }, [user?.grade, gradeOptions]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleDateChange = (e) => {
    setUser((prevState) => ({
      ...prevState,
      birth_date: e.value,
    }));
  };

  const handleDropdownChange = (e, fieldName) => {
    setUser((prevState) => ({
      ...prevState,
      [fieldName]: e.value,
    }));
  };

  const handleGradeChange = (e) => {
    const selectedId = e.value;
    const found = gradeOptions.find(opt => opt.value === selectedId);
    setUser((prevState) => ({
      ...prevState,
      gradeId: selectedId || null,
      grade: found?.name || found?.label || ''
    }));
  };

  const genderOptions = [
    { label: 'Select Gender', value: '' },
    { label: 'Male', value: 'Male' },
    { label: 'Female', value: 'Female' },
    { label: 'Other', value: 'Other' }
  ];

  return (
    <div className="student-details-section border-1 border-gray-300 border-round-md p-3 w-full">
      <h4 className="text-center text-2xl">Student Information</h4>
      <div className="grid">
        {/* Full Name */}
        <div className="col-12 md:col-6">
          <div className="p-field ">
            <label htmlFor="full_name" className="text-left ">Full Name</label>
            <InputText
              id="full_name"
              name="full_name"
              value={user.full_name || ''}
              onChange={handleChange}
              placeholder="Enter student's full name "
              className="w-full border-1 border-gray-400"
            />
          </div>
        </div>

        {/* Grade */}
        <div className="col-12 md:col-6">
          <div className="p-field -mb-3">
            <label htmlFor="gradeId">Grade/Class</label>
            <Dropdown
              id="gradeId"
              name="gradeId"
              value={user.gradeId ?? null}
              options={gradeOptions}
              onChange={handleGradeChange}
              placeholder="Select Grade/Class"
              optionLabel="label"
              optionValue="value"
              className="w-full border-1 border-gray-400"
            />
          </div>
        </div>


        <div className='flex justify-content-center align-items-center gap-3 col-12'>
        {/* Gender */}
        {/* <div className=""> */}
          <div className="col-12 md:col-6 p-field mb-2 ">
            <label htmlFor="gender">Gender</label>
            <Dropdown
              id="gender"
              name="gender"
              value={user.gender || ''}
              options={genderOptions}
              onChange={(e) => handleDropdownChange(e, 'gender')}
              placeholder="Select gender"
              optionLabel="label"
              optionValue="value"
              className="w-full border-1 border-gray-400" 
            />
          </div>
        {/* </div> */}

        {/* Birth Date */}
          <div className="col-12 md:col-6 p-field mb-2 ">
            <label htmlFor="birth_date" className="text-left">Birth Date</label>
            <Calendar
              id="birth_date"
              name="birth_date"
              value={user.birth_date ? new Date(user.birth_date) : null}
              onChange={handleDateChange}
              placeholder="Select birth date"
              dateFormat="yy-mm-dd"
              showIcon
              maxDate={new Date()}
              className="w-full border-1 border-gray-400"
            />
          </div>
        </div>
      </div>

      {/* Parent Selection (kept hidden) */}
      {/* <div className="p-field mb-2">
        <label htmlFor="parent_id">Parent/Guardian</label>
        <Dropdown
          id="parent_id"
          name="parent_id"
          value={user.parent_id || null}
          options={parents}
          onChange={(e) => handleDropdownChange(e, 'parent_id')}
          placeholder={loading ? "Loading parents..." : "Select parent/guardian"}
          optionLabel="label"
          optionValue="value"
          className="w-full"
          disabled={loading}
        />
        <small className="p-text-secondary">
          Optional: Select a parent/guardian if applicable
        </small>
      </div> */}
    </div>
  );
};

export default StudentDetails;
